import {
  useMutation,
  useQueryClient,
  useQuery,
  QueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { REQUIRED_MESSAGE } from "@/utils/constant";
import { axiosInstance } from "@/utils/lib/axios";
import { LoaderFunction } from "react-router-dom";

const profileEditFormSchema = z.object({
  company: z.string().min(1, { message: REQUIRED_MESSAGE }),
  companyCode: z.string().nullable(),
  email: z.string().min(1, { message: REQUIRED_MESSAGE }),
  department: z.string().optional(),
  phone: z
    .string()
    .max(13, { message: "Phone number can't be more than 13 digits" })
    .refine((value) => !value || /^\d+$/.test(value), {
      message: "Phone number should only contain numbers",
    })
    .optional(),
  address: z.string().optional(),
});
type ProfileEditFormSchema = z.infer<typeof profileEditFormSchema>;

type ProfileData = {
  id: string;
  company: string;
  department?: string;
  companyCode: string | null;
  email: string;
  phone?: string;
  address?: string;
  role: string;
};

/* ============== *
 *  READ profile *
 * ============== */

async function getProfile(): Promise<ProfileData> {
  const response = await axiosInstance.get(`/api/auth/profile`);
  return response.data.data;
}

function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
  });
}

function profileLoader(queryClient: QueryClient): LoaderFunction<ProfileData> {
  return async () => {
    const profile = await queryClient.ensureQueryData({
      queryKey: ["profile"],
      queryFn: getProfile,
    });

    return profile;
  };
}

/* ============== *
 * UPDATE profile *
 * ============== */

async function editProfile(data: ProfileEditFormSchema): Promise<ProfileData> {
  const response = await axiosInstance.post(`/api/crud/user/profile`, data);
  return response.data;
}

function useProfileEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: ProfileEditFormSchema }) =>
      editProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["profile"],
      });
    },
  });
}

export type { ProfileEditFormSchema };
export {
  profileEditFormSchema,
  useProfileEdit,
  getProfile,
  useProfile,
  profileLoader,
};
